import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import axios from '../../../utils/axios';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AdminPanel = ({ adminId, adminRole }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [schoolId, setSchoolId] = useState('');
  const [events, setEvents] = useState([]);
  const [students, setStudents] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [schoolName, setSchoolName] = useState('');
  const [facultyId, setFacultyId] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [studentToEdit, setStudentToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: '',
    student_id: ''
  });
  const [formError, setFormError] = useState('');
  const [studentLoading, setStudentLoading] = useState(false);
  const [faculty, setFaculty] = useState([]);
  const [selectedFaculty, setSelectedFaculty] = useState(null);
  const [classes, setClasses] = useState([]);
  const [facultyLoading, setFacultyLoading] = useState(false);
  const [classesLoading, setClassesLoading] = useState(false);
  // Add search state variables
  const [eventSearch, setEventSearch] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [facultySearch, setFacultySearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const storedSchoolId = localStorage.getItem('schoolId');
        const storedFacultyId = localStorage.getItem('facultyId');
        
        if (!storedSchoolId || !storedFacultyId) {
          throw new Error('School or faculty ID not found');
        }
        
        setSchoolId(storedSchoolId);
        setFacultyId(storedFacultyId);
        
        // Fetch school information
        const schoolResponse = await axios.get(`/api/schools/${storedSchoolId}/`);
        setSchoolName(schoolResponse.data.name);
        
        // Fetch all events for this school
        const eventsResponse = await axios.get(`/api/events/?school=${storedSchoolId}`);
        
        // Filter events by school ID to ensure we only show events from this university
        const filteredEvents = eventsResponse.data.filter(event => 
          String(event.school) === String(storedSchoolId)
        );
        
        // Get faculty information for each event
        const eventsWithFaculty = await Promise.all(filteredEvents.map(async (event) => {
          try {
            // Fetch faculty information for this event
            const facultyResponse = await axios.get(`/api/faculty/${event.faculty}/`);
            return {
              ...event,
              facultyName: `${facultyResponse.data.first_name} ${facultyResponse.data.last_name}`
            };
          } catch (err) {
            console.error(`Failed to fetch faculty info for event ${event.id}:`, err);
            return {
              ...event,
              facultyName: 'Unknown Faculty'
            };
          }
        }));
        
        // Sort events by date (nearest first)
        const sortedEvents = [...eventsWithFaculty].sort((a, b) => {
          return new Date(a.date) - new Date(b.date);
        });
        
        setEvents(sortedEvents);
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading admin data:', err);
        setError('Failed to load admin data. Please try again later.');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // New function to fetch students when the tab changes to student management
  const fetchStudents = async () => {
    try {
      setStudentLoading(true);
      // Fetch all students for this school
      const studentsResponse = await axios.get(`/api/students/`);
      
      // Filter students to only include those from this school
      const filteredStudents = studentsResponse.data.filter(student => 
        String(student.school) === String(schoolId)
      );
      
      // Sort by last name
      const sortedStudents = [...filteredStudents].sort((a, b) => 
        a.last_name.localeCompare(b.last_name)
      );
      
      setStudents(sortedStudents);
      setStudentLoading(false);
    } catch (err) {
      console.error('Error loading students:', err);
      setError('Failed to load student data. Please try again later.');
      setStudentLoading(false);
    }
  };

  // Function to fetch faculty members by school
  const fetchFacultyBySchool = async () => {
    try {
      setFacultyLoading(true);
      setError('');
      
      const response = await axios.get('/api/faculty-by-school/', {
        params: {
          school_id: schoolId,
          faculty_id: facultyId
        }
      });
      
      setFaculty(response.data);
      setFacultyLoading(false);
    } catch (err) {
      console.error('Error fetching faculty:', err);
      setError(err.response?.data?.error || 'Failed to load faculty members');
      setFacultyLoading(false);
    }
  };

  // Function to fetch classes by faculty
  const fetchClassesByFaculty = async (facultyId) => {
    try {
      setClassesLoading(true);
      setError('');
      
      const response = await axios.get('/api/classes-by-faculty/', {
        params: {
          faculty_id: facultyId,
          requester_id: localStorage.getItem('facultyId')
        }
      });
      
      setClasses(response.data);
      setClassesLoading(false);
    } catch (err) {
      console.error('Error fetching classes:', err);
      setError(err.response?.data?.error || 'Failed to load classes');
      setClassesLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // If changing to the student management tab, fetch students
    if (newValue === 1) {
      fetchStudents();
    }
    // If changing to class management tab, fetch faculty
    else if (newValue === 2) {
      fetchFacultyBySchool();
      setSelectedFaculty(null); // Reset selected faculty
      setClasses([]); // Reset classes
    }
    // Clear any existing messages when changing tabs
    setError('');
    setSuccess('');
    // Clear search terms when switching tabs
    setEventSearch('');
    setStudentSearch('');
    setFacultySearch('');
  };

  const handleEditEvent = (eventId) => {
    navigate(`/faculty/events/${eventId}/edit`);
  };

  const handleOpenDeleteDialog = (event) => {
    setEventToDelete(event);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setEventToDelete(null);
  };

  const handleDeleteEvent = async () => {
    if (!eventToDelete) return;
    
    try {
      // When deleting as admin, we need to make sure we're passing the faculty_id param
      // to let the backend know who is deleting the event
      await axios.delete(`/api/events/${eventToDelete.id}/`, {
        params: { faculty_id: facultyId }
      });
      
      // Remove the deleted event from the state
      setEvents(prevEvents => prevEvents.filter(e => e.id !== eventToDelete.id));
      
      // Show success message
      setError(null);
      setSuccess('Event deleted successfully');
      
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting event:', err);
      if (err.response?.status === 403) {
        setError('You do not have permission to delete this event. This may be due to a permissions issue with your administrator account.');
      } else {
        setError(err.response?.data?.error || 'Failed to delete event. Please try again.');
      }
    }
  };

  // Student management handlers
  const handleEditStudent = (student) => {
    setStudentToEdit(student);
    setEditFormData({
      first_name: student.first_name,
      last_name: student.last_name,
      student_id: student.student_id
    });
    setEditDialogOpen(true);
  };

  const handleOpenDeleteStudentDialog = (student) => {
    setStudentToDelete(student);
    setDeleteDialogOpen(true);
  };

  const handleEditFormChange = (e) => {
    setEditFormData({
      ...editFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleSaveStudentEdit = async () => {
    if (!studentToEdit) return;
    
    try {
      // Basic validation
      if (!editFormData.first_name || !editFormData.last_name || !editFormData.student_id) {
        setFormError('All fields are required');
        return;
      }
      
      const response = await axios.put(`/api/students/${studentToEdit.id}/update/`, editFormData);
      
      // Update the student in the state
      setStudents(prevStudents => 
        prevStudents.map(s => 
          s.id === studentToEdit.id ? {...s, ...response.data} : s
        )
      );
      
      setSuccess('Student information updated successfully');
      setEditDialogOpen(false);
      setFormError('');
    } catch (err) {
      console.error('Error updating student:', err);
      setFormError(err.response?.data?.error || 'Failed to update student information');
    }
  };

  const handleDeleteStudent = async () => {
    if (!studentToDelete) return;
    
    try {
      await axios.delete(`/api/students/${studentToDelete.id}/delete/`);
      
      // Remove the student from the state
      setStudents(prevStudents => prevStudents.filter(s => s.id !== studentToDelete.id));
      
      setSuccess('Student account deleted successfully');
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error('Error deleting student:', err);
      setError(err.response?.data?.error || 'Failed to delete student account');
      setDeleteDialogOpen(false);
    }
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const isOwnEvent = (event) => {
    return String(event.faculty) === String(facultyId);
  };

  // Handler for selecting a faculty member
  const handleFacultySelect = (faculty) => {
    setSelectedFaculty(faculty);
    fetchClassesByFaculty(faculty.id);
  };

  // Handler for viewing class details
  const handleViewClass = (classId) => {
    navigate(`/faculty/classes/${classId}`);
  };

  // Filter functions for search
  const filteredEvents = events.filter(event => 
    event.name.toLowerCase().includes(eventSearch.toLowerCase())
  );

  const filteredStudents = students.filter(student => 
    `${student.first_name} ${student.last_name}`.toLowerCase().includes(studentSearch.toLowerCase())
  );

  const filteredFaculty = faculty.filter(f => 
    `${f.first_name} ${f.last_name}`.toLowerCase().includes(facultySearch.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#DEA514' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography 
        variant="h4" 
        sx={{ 
          mb: 1,
          color: '#2C2C2C',
          fontWeight: 'bold'
        }}
      >
        Administrator Panel
      </Typography>
      
      <Typography 
        variant="subtitle1" 
        sx={{ 
          mb: 3, 
          color: '#666',
        }}
      >
        {schoolName} - University Administrator
      </Typography>
      
      <Paper sx={{ width: '100%', bgcolor: 'white' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{
            '& .MuiTab-root.Mui-selected': {
              color: '#DEA514',
              fontWeight: 'bold',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#DEA514',
            },
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Tab label="Events Management" />
          <Tab label="Student Management" />
          <Tab label="Class Management" />
          <Tab label="Admin Information" />
        </Tabs>
        
        <TabPanel value={tabValue} index={0}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          
          <Typography variant="h6" sx={{ mb: 2 }}>
            {schoolName} Events
          </Typography>
          
          {/* Add event search bar */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search events by name..."
            value={eventSearch}
            onChange={(e) => setEventSearch(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Event Name</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEvents.length > 0 ? (
                  filteredEvents.map((event) => {
                    const datetime = formatDateTime(event.date);
                    return (
                      <TableRow key={event.id}>
                        <TableCell>{event.name}</TableCell>
                        <TableCell>
                          {datetime.date}{' '}
                          {datetime.time}
                        </TableCell>
                        <TableCell>
                          {isOwnEvent(event) ? (
                            'You (Creator)'
                          ) : (
                            event.facultyName || 'Another Faculty'
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton 
                            color="primary" 
                            onClick={() => handleEditEvent(event.id)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            color="error"
                            onClick={() => handleOpenDeleteDialog(event)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      {events.length > 0 ? 'No events match your search' : 'No events found for this school'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          
          <Typography variant="h6" sx={{ mb: 2 }}>
            {schoolName} Students
          </Typography>
          
          {/* Add student search bar */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search students by name..."
            value={studentSearch}
            onChange={(e) => setStudentSearch(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          {studentLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#DEA514' }} />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Student ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredStudents.length > 0 ? (
                    filteredStudents.map((student) => (
                      <TableRow key={student.id}>
                        <TableCell>{student.student_id}</TableCell>
                        <TableCell>{`${student.first_name} ${student.last_name}`}</TableCell>
                        <TableCell>{student.email}</TableCell>
                        <TableCell>
                          <IconButton 
                            color="primary" 
                            onClick={() => handleEditStudent(student)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            color="error"
                            onClick={() => handleOpenDeleteStudentDialog(student)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        {students.length > 0 ? 'No students match your search' : 'No students found for this school'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Class Management
          </Typography>
          
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            {/* Faculty List */}
            <Paper sx={{ width: { xs: '100%', md: '40%' }, p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Faculty Members
              </Typography>
              
              {/* Add faculty search bar */}
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search faculty by name..."
                value={facultySearch}
                onChange={(e) => setFacultySearch(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
              
              {facultyLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress sx={{ color: '#DEA514' }} />
                </Box>
              ) : (
                <Box>
                  {filteredFaculty.length > 0 ? (
                    <Box sx={{ maxHeight: '500px', overflow: 'auto' }}>
                      {filteredFaculty.map((f) => (
                        <Paper
                          key={f.id}
                          elevation={selectedFaculty?.id === f.id ? 3 : 1}
                          sx={{
                            p: 2,
                            mb: 1,
                            cursor: 'pointer',
                            backgroundColor: selectedFaculty?.id === f.id ? '#f5f5f5' : 'white',
                            transition: 'all 0.2s',
                            '&:hover': {
                              backgroundColor: '#f9f9f9',
                            }
                          }}
                          onClick={() => handleFacultySelect(f)}
                        >
                          <Typography variant="body1" fontWeight="bold">
                            {f.first_name} {f.last_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {f.email}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Classes: {f.class_count}
                            </Typography>
                            {String(f.id) === String(facultyId) && (
                              <Typography variant="caption" color="primary">
                                (You)
                              </Typography>
                            )}
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                      {faculty.length > 0 ? 'No faculty members match your search' : 'No faculty members found'}
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>

            {/* Classes List - no change needed here since filtering is handled in the faculty list */}
            <Paper sx={{ width: { xs: '100%', md: '60%' }, p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedFaculty ? `Classes taught by ${selectedFaculty.first_name} ${selectedFaculty.last_name}` : 'Select a faculty member to view their classes'}
              </Typography>
              
              {classesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress sx={{ color: '#DEA514' }} />
                </Box>
              ) : (
                <Box>
                  {selectedFaculty ? (
                    classes.length > 0 ? (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Class Name</TableCell>
                              <TableCell>Semester</TableCell>
                              <TableCell>Students</TableCell>
                              <TableCell>Events</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {classes.map((c) => (
                              <TableRow key={c.id}>
                                <TableCell>{c.name}</TableCell>
                                <TableCell>{c.semester || 'N/A'}</TableCell>
                                <TableCell>{c.student_count}</TableCell>
                                <TableCell>{c.event_count}</TableCell>
                                <TableCell>
                                  <Button 
                                    variant="outlined" 
                                    size="small"
                                    onClick={() => handleViewClass(c.id)}
                                  >
                                    View
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                        No classes found for this faculty member
                      </Typography>
                    )
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                      <Typography variant="body1" color="text.secondary">
                        Select a faculty member from the list to view their classes
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          </Box>
        </TabPanel>
        
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Administrator Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Role
              </Typography>
              <Typography variant="body1">
                University Administrator (Sub-Admin)
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                School
              </Typography>
              <Typography variant="body1">
                {schoolName}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Permissions
              </Typography>
              <Typography variant="body1">
                As a University Administrator, you can view, edit, and delete any events within your university.
              </Typography>
            </Box>
          </Box>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            Your administrator credentials are linked to your faculty account. For more information or to request additional permissions, please contact a master administrator.
          </Alert>
        </TabPanel>
      </Paper>
      
      {/* Delete Event Dialog */}
      <Dialog
        open={deleteDialogOpen && eventToDelete}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Event
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the event "{eventToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteEvent} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Student Dialog */}
      <Dialog
        open={deleteDialogOpen && studentToDelete}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-student-dialog-title"
      >
        <DialogTitle id="delete-student-dialog-title">
          Delete Student Account
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete {studentToDelete?.first_name} {studentToDelete?.last_name}'s account? 
            This will remove all their attendance records and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteStudent} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit Student Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        aria-labelledby="edit-student-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="edit-student-dialog-title">
          Edit Student Information
        </DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>
          )}
          
          <TextField
            margin="normal"
            label="First Name"
            name="first_name"
            value={editFormData.first_name}
            onChange={handleEditFormChange}
            fullWidth
            required
          />
          
          <TextField
            margin="normal"
            label="Last Name"
            name="last_name"
            value={editFormData.last_name}
            onChange={handleEditFormChange}
            fullWidth
            required
          />
          
          <TextField
            margin="normal"
            label="Student ID"
            name="student_id"
            value={editFormData.student_id}
            onChange={handleEditFormChange}
            fullWidth
            required
          />
          
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Note: Email addresses cannot be changed to maintain account security
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveStudentEdit} color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPanel;
